/**
 * The capacity heatmap (PRD 10.3; Phase 23), shared by the server read model and the browser
 * client so the two cannot disagree on what a day column is, how a week's hours are split over
 * its days, or what the threshold scale says.
 *
 * The heatmap is the one view drawn in the reserved status vocabulary alone (PRD 7.4): every
 * cell is a band from the run's thresholds, so a cell names a colour in `palette.js` directly
 * and no project-type hue appears. The four bands are separated by lightness, not hue, and
 * every cell carries the band's icon; the word is in the legend and the tooltip.
 *
 * Pure: no DOM, no I/O.
 */
import { weekdaysIn } from '../weeks.js';
import { col } from './table.js';
import { STATUS_STATES } from './palette.js';
import { bandFor } from './dashboard.js';

/** The filters the heatmap reads, in the API's names (grain is decided here, not sent). */
export const HEATMAP_PARAM_KEYS = Object.freeze([
  'run_id',
  'from',
  'weeks',
  'dimension',
  'pipeline',
]);

/** The heatmap has no project dimension: a project's hours against company capacity would mislead. */
export const HEATMAP_DIMENSIONS = Object.freeze([
  'company',
  'department',
  'service',
  'office',
  'individual',
]);

/** The grains the API answers; every other grain in the filter bar draws weekly with a note. */
export const HEATMAP_GRAINS = Object.freeze(['week', 'day']);

/**
 * The dimension the heatmap opens on when the shared filters are at their default (2026-09-29).
 * The filters' own default is the company, which on this page is one row — and that row is the
 * foot of every other dimension's matrix anyway. By service is the reading the page is for. A
 * link that names a dimension is left alone; only the default is replaced, on arrival.
 */
export const HEATMAP_DEFAULT_DIMENSION = 'service';

/** The daily grain covers the 14-day window (PRD 7.2, TRD 9): two weeks, ten weekdays. */
export const DAY_GRAIN_WEEKS = 2;

export const WEEKDAY_LABELS = Object.freeze(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * The day columns of a set of week windows: the weekdays of each, Monday to Friday, with the
 * week they belong to. Weekends hold no capacity and no demand, so they are not columns.
 * @param {{ start: string, end: string }[]} windows
 * @returns {{ date: string, weekday: string, week_start: string }[]}
 */
export function dayColumns(windows) {
  return windows.flatMap((w) =>
    weekdaysIn(w.start, w.end).map((date) => ({
      date,
      weekday: WEEKDAY_LABELS[new Date(`${date}T00:00:00Z`).getUTCDay() - 1] ?? date,
      week_start: w.start,
    })),
  );
}

/**
 * Split `total` over slots in proportion to `weights`, to two decimals, summing to exactly
 * `round2(total)`: the rounding remainder lands on the last slot with a weight. Weights that are
 * all zero (or missing) split evenly, so a week nobody can work still puts its hours somewhere
 * the reader can see them. No slots, no shares.
 * @param {number} total
 * @param {number[]} weights
 * @returns {number[]}
 */
export function distribute(total, weights) {
  const n = weights.length;
  if (n === 0) return [];
  const t = round2(Number(total) || 0);
  let w = weights.map((x) => Math.max(0, Number(x) || 0));
  let sum = w.reduce((s, x) => s + x, 0);
  if (sum <= 0) {
    w = weights.map(() => 1);
    sum = n;
  }
  const out = w.map((x) => round2((t * x) / sum));
  const last = w.reduce((idx, x, i) => (x > 0 ? i : idx), -1);
  const drift = round2(t - out.reduce((s, x) => s + x, 0));
  if (drift !== 0 && last >= 0) out[last] = round2(out[last] + drift);
  return out;
}

/**
 * The threshold colour scale with its labels (PRD 10.3): the four bands in order, each with the
 * range of allocation it covers under the run's thresholds, the reserved colour, the icon and
 * the word. `range` is what the legend prints; `min`/`max` are the bounds (null when open).
 * @param {{ underload_below: number, caution_above: number, overloaded_above: number }} thresholds
 */
export function thresholdScale(thresholds) {
  const t = thresholds ?? { underload_below: 0.85, caution_above: 1, overloaded_above: 1.15 };
  const pct = (v) => `${Math.round(v * 100)}%`;
  const bounds = {
    underload: { min: null, max: t.underload_below, range: `below ${pct(t.underload_below)}` },
    healthy: {
      min: t.underload_below,
      max: t.caution_above,
      range: `${pct(t.underload_below)} to ${pct(t.caution_above)}`,
    },
    caution: {
      min: t.caution_above,
      max: t.overloaded_above,
      range: `over ${pct(t.caution_above)} to ${pct(t.overloaded_above)}`,
    },
    overloaded: { min: t.overloaded_above, max: null, range: `over ${pct(t.overloaded_above)}` },
  };
  return STATUS_STATES.map((s) => ({ ...s, ...bounds[s.key] }));
}

/** The band a utilization falls in under the thresholds (the engine's rule, restated once). */
export const bandOf = bandFor;

/** The cell with the highest allocation in a list (ties to the earlier one); null with none. */
export function peakCell(cells) {
  let best = null;
  for (const c of cells ?? []) {
    const u = num(c.utilization);
    if (u === null) continue;
    if (best === null || u > num(best.utilization)) best = c;
  }
  return best;
}

/** The average allocation over the cells with capacity, weighted by capacity; null with none. */
export function averageOf(cells) {
  let demand = 0;
  let capacity = 0;
  for (const c of cells ?? []) {
    if (!(num(c.capacity_hours) > 0)) continue;
    demand += num(c.allocated_hours) ?? 0;
    capacity += num(c.capacity_hours);
  }
  return capacity > 0 ? Math.round((demand / capacity) * 10000) / 10000 : null;
}

export const ROW_SORTS = Object.freeze(['label', 'peak', 'average']);

/**
 * Order heatmap rows: `label` is the API's order (its label sort), `peak` and `average` descend
 * by the row's peak or average allocation with rows without capacity last, ties by label.
 * Never mutates the input.
 * @param {{ label: string, cells: object[] }[]} rows
 * @param {'label' | 'peak' | 'average'} by
 * @param {'cells' | 'days'} [field]  which period set to measure
 */
export function sortHeatmapRows(rows, by = 'label', field = 'cells') {
  const list = [...(rows ?? [])];
  if (by === 'label') return list;
  const measure = (r) =>
    by === 'peak' ? num(peakCell(r[field])?.utilization) : averageOf(r[field]);
  return list
    .map((r, i) => ({ r, i, m: measure(r) }))
    .sort((a, b) => {
      if (a.m === null && b.m === null) return a.i - b.i;
      if (a.m === null) return 1;
      if (b.m === null) return -1;
      return b.m - a.m || a.i - b.i;
    })
    .map((x) => x.r);
}

/**
 * What a cell prints: the allocation as a percentage, or, with no capacity, the hours that have
 * nowhere to go (a dash when there are none of those either).
 * @param {{ utilization: number | null, allocated_hours: number }} cell
 */
export function cellText(cell) {
  const u = num(cell?.utilization);
  if (u !== null) return `${Math.round(u * 100)}%`;
  const h = num(cell?.allocated_hours) ?? 0;
  return h > 0 ? `${h % 1 === 0 ? h : h.toFixed(1)} h` : '—';
}

/**
 * The figures the card's header states: rows drawn, cells per band, and the peak cell with the
 * row it sits in. Works for either grain (`field` picks weeks or days).
 * @param {{ rows: object[] }} data
 * @param {'cells' | 'days'} [field]
 */
export function heatmapSummary(data, field = 'cells') {
  const rows = data?.rows ?? [];
  const perBand = Object.fromEntries(STATUS_STATES.map((s) => [s.key, 0]));
  let noCapacity = 0;
  let peak = null;
  for (const r of rows) {
    for (const c of r[field] ?? []) {
      if (c.band) perBand[c.band] += 1;
      else noCapacity += 1;
      const u = num(c.utilization);
      if (u !== null && (peak === null || u > num(peak.cell.utilization)))
        peak = { row: r, cell: c };
    }
  }
  return { rows: rows.length, per_band: perBand, no_capacity: noCapacity, peak };
}

/**
 * The table view's rows: one per row and period, long format, with the same numbers as the
 * cells. `period` is the week start or the day.
 * @param {{ dimension: string, rows: object[], totals?: object[], totals_days?: object[] }} data
 * @param {'week' | 'day'} grain
 */
export function heatmapExportRows(data, grain = 'week') {
  const field = grain === 'day' ? 'days' : 'cells';
  const periodOf = (c) => (grain === 'day' ? c.date : c.week_start);
  const out = [];
  const push = (label, key, c) =>
    out.push({
      row: label,
      key,
      period: periodOf(c),
      week_start: c.week_start,
      allocated_hours: c.allocated_hours,
      capacity_hours: c.capacity_hours,
      headroom_hours: c.headroom_hours,
      utilization: c.utilization,
      band: c.band,
      explicit_hours: c.explicit_hours,
      inferred_hours: c.inferred_hours,
      recurring_hours: c.recurring_hours,
      nonproject_hours: c.nonproject_hours,
      pipeline_hours: c.pipeline_hours,
      high_confidence_hours: c.high_confidence_hours,
      exception_hours: c.exception_hours ?? null,
      people: c.people,
      people_off: c.people_off ?? null,
      projects: c.projects,
      opportunities: c.opportunities,
      unassigned_hours: c.unassigned_hours ?? null,
    });
  for (const r of data?.rows ?? []) for (const c of r[field] ?? []) push(r.label, r.key, c);
  const totals = grain === 'day' ? data?.totals_days : data?.totals;
  for (const c of totals ?? []) push('Company', 'company', c);
  return out;
}

/** The noun a heatmap row wears, singular then plural, per dimension. */
export const ROW_NOUNS = Object.freeze({
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
});

/**
 * The table view's columns (Phase 37). A specification, not a rendering: the screen decorates it
 * with `withFormats` and the CSV writer reads the same list, so an export cannot hold a different
 * set of columns from the table it came from.
 *
 * @param {{ dimension?: string, grain?: 'week' | 'day' }} opts
 */
export function heatmapColumns({ dimension = 'company', grain = 'week' } = {}) {
  const daily = grain === 'day';
  const period = daily ? col.date('period', 'Day') : col.week('period', 'Week');
  return [
    col.text('row', (ROW_NOUNS[dimension] ?? ROW_NOUNS.company)[0]),
    period,
    col.hours('allocated_hours', 'Allocated'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('headroom_hours', 'Headroom'),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.hours('explicit_hours', 'Explicit'),
    col.hours('inferred_hours', 'Inferred'),
    col.hours('recurring_hours', 'Recurring'),
    col.hours('nonproject_hours', 'Non-project'),
    col.hours('pipeline_hours', 'Pipeline'),
    col.hours('high_confidence_hours', 'High confidence'),
    ...(daily
      ? [col.hours('exception_hours', 'Time off'), col.count('people_off', 'People out')]
      : []),
    col.count('people', 'People'),
    col.count('projects', 'Projects'),
    col.count('opportunities', 'Deals'),
    col.hours('unassigned_hours', 'Role-only'),
  ];
}
